/**
 * One place that applies the lens to one section.
 *
 * The composition rule lives in `sectionEvidence.ts`; this is the wrapper that
 * calls it, so no page re-implements "should this be drawn". Two things it
 * does beyond hiding:
 *
 *   · It carries the LIMITATION note for an aggregate that cannot be split by
 *     performed role. Phase 5 of the directive is explicit that such a figure
 *     must be labelled rather than quietly presented as belonging to the
 *     selected lens.
 *
 *   · It renders the children unchanged when the section is truthful, so a
 *     section never has to know it is inside a lens.
 */

import type { ReactNode } from 'react';
import {
  isSectionTruthfulForLens,
  lensLimitationNote,
} from '@/lib/passport/sectionEvidence';
import type { PassportWorkLens } from '@/lib/passport/workLens';

export interface PassportLensSectionProps {
  /** Must match a key in `PASSPORT_SECTION_EVIDENCE`. */
  sectionId: string;
  lens: PassportWorkLens | null;
  children: ReactNode;
}

export function PassportLensSection({
  sectionId,
  lens,
  children,
}: PassportLensSectionProps) {
  if (!isSectionTruthfulForLens(sectionId, lens)) return null;

  const note = lensLimitationNote(sectionId, lens);
  if (!note) return <>{children}</>;

  return (
    <div data-lens-section={sectionId}>
      {children}
      <p
        className="mt-1 px-1 text-[11px] text-muted-foreground"
        data-testid={`lens-limitation-${sectionId}`}
      >
        {note}
      </p>
    </div>
  );
}
